import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { datasetService, DATASET_API_PATHS } from '../services/dataset.service'
import type {
  Dataset,
  DatasetListResponse,
  GetDatasetListRequest,
} from '../types/dataset.type'

/**
 * 获取数据集列表 Hook
 * @param params 查询参数（知识库ID、搜索关键词、状态等）
 * @returns SWR 查询对象，包含 data、error、mutate 等
 */
export const useDatasetList = (params: GetDatasetListRequest) => {
  // 构建唯一的key，使用 JSON.stringify 确保对象参数变化时 key 能正确更新
  const key = [DATASET_API_PATHS.LIST, JSON.stringify(params)]

  return useSWR<DatasetListResponse>(
    key,
    () => datasetService.getDatasetList(params),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  )
}

/**
 * 删除数据集 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useDeleteDataset = () => {
  return useSWRMutation<boolean, Error, string, string>(
    DATASET_API_PATHS.DELETE,
    (_, { arg }) => datasetService.deleteDataset(arg)
  )
}

/**
 * 新增/更新数据集 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useUpdateDataset = () => {
  return useSWRMutation<{ id: string }, Error, string, Partial<Dataset>>(
    DATASET_API_PATHS.SAVE_OR_UPDATE,
    (_, { arg }) => datasetService.updateDataset(arg)
  )
}

/**
 * 切换数据集状态 Hook
 * @returns SWR mutation 对象，包含 trigger、isMutating、error
 */
export const useToggleDatasetState = () => {
  return useSWRMutation<boolean, Error, string, { id: string; status: number }>(
    DATASET_API_PATHS.TOGGLE_STATE,
    (_, { arg }) => datasetService.toggleDatasetState(arg.id, arg.status)
  )
}

/**
 * 数据集操作组合 Hook
 * 提供完整的数据集管理功能，包括列表获取、新增、编辑、删除、状态切换
 * 所有操作完成后会自动刷新列表
 * @param params 列表查询参数
 * @returns 包含所有数据集操作方法的对象
 */
export const useDataset = (params: GetDatasetListRequest) => {
  const { data, error, mutate } = useDatasetList(params)
  const { trigger: updateDataset, isMutating: isUpdating } = useUpdateDataset()
  const { trigger: deleteDataset, isMutating: isDeleting } = useDeleteDataset()
  const { trigger: toggleState, isMutating: isToggling } =
    useToggleDatasetState()

  /**
   * 新增或更新数据集
   * @param datasetData 数据集数据（新增时不传id，编辑时必传id）
   * @returns Promise<{ id: string }> 返回包含id的对象
   */
  const saveOrUpdateDataset = async (datasetData: Partial<Dataset>) => {
    const result = await updateDataset(datasetData)
    // 操作成功后刷新列表
    await mutate()
    return result
  }

  /**
   * 删除数据集
   * @param id 数据集ID
   * @returns Promise<boolean>
   */
  const removeDataset = async (id: string) => {
    const result = await deleteDataset(id)
    // 操作成功后刷新列表
    await mutate()
    return result
  }

  /**
   * 切换数据集状态
   * @param id 数据集ID
   * @param newStatus 新状态
   * @returns Promise<boolean>
   */
  const toggleDatasetStatus = async (id: string, newStatus: number) => {
    const result = await toggleState({ id, status: newStatus })
    // 操作成功后刷新列表
    await mutate()
    return result
  }

  return {
    // 列表数据
    datasetList: data?.datasets || [],
    isLoading: !data && !error,
    error,

    // 操作方法
    saveOrUpdateDataset,
    removeDataset,
    toggleDatasetStatus,

    // 加载状态
    isUpdating,
    isDeleting,
    isToggling,

    // 刷新方法
    refresh: mutate,
  }
}
